import React, { useState, useEffect, useRef } from 'react'; // Import useRef
import { Container, Row, Col, Card, Table, Form, Button, Alert, Spinner, Badge, Dropdown, ListGroup } from 'react-bootstrap'; // Add Dropdown, ListGroup
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import { agentApi, notificationApi } from '../../services/api'; // Add notificationApi
import {
  FaArrowLeft,
  FaFileInvoice,
  FaCheckCircle,
  FaInfoCircle,
  FaSearch, // Add icons used in header
  FaTimes,
  FaUser,
  FaSignOutAlt,
  FaBell,
  FaQuestionCircle
} from 'react-icons/fa';
import AgentSidebar from '../../components/AgentSidebar';
import { useAuth } from '../../hooks/useAuth'; // Import useAuth
import './Dashboard.css';
// Import Products.css if header styles are defined there
// import './Products.css'; // Uncomment if needed

// Helper function to format currency
const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return '₹0';
  return `₹${amount.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

// Helper function to format dates
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    const formattedDate = date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long'
    });
    const formattedTime = date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    }).replace(/^(\d+:\d+)(:\d+)?\s*/, '$1 '); // Clean seconds if present
    return `${formattedDate}, ${formattedTime}`;
  } catch (e) {
    return dateString; // Fallback
  }
};

const CreateInvoice = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { getCurrentUser, logout } = useAuth(); // Get auth functions
  const currentUser = getCurrentUser(); // Get current user
  const printableRef = useRef(null); // Define printableRef
  const selectedCaseIds = location.state?.selectedCaseIds || [];
  const [cases, setCases] = useState([]);
  const [selectedCases, setSelectedCases] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [totalAmount, setTotalAmount] = useState(0);
  
  // State for form submission
  const [loading, setLoading] = useState({
    // payout: true, // Removed payout loading state
    cases: true,
    submit: false
  });
  const [error, setError] = useState({
    // payout: '', // Removed payout error state
    cases: '',
    submit: ''
  });
  const [success, setSuccess] = useState('');

  // --- State for Header elements (copied from Products.js) ---
  const [searchQuery, setSearchQuery] = useState(''); // Add search state if needed
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const notificationRef = useRef(null);
  
  // Fetch eligible cases for invoice creation
  useEffect(() => {
    const fetchEligibleCases = async () => { // Renamed function
      setLoading(prev => ({ ...prev, cases: true }));
      setError(prev => ({ ...prev, cases: '' }));
      
      try {
        // Fetch cases with status 13 (Approved by RM/Backoffice)
        // Use a high limit to get all potentially eligible cases
        const response = await agentApi.getApprovedPayoutCases({ 
          limit: 1000, // Fetch more cases
          status: 13   // Filter for status 13
        });
        // Filter out cases that might already be part of a payout (optional, backend should handle this ideally)
        // For now, assume the backend returns only truly eligible cases based on status 13
        const eligibleCases = response?.data?.items || []; // Use optional chaining for safer access
        setCases(eligibleCases);
      } catch (err) {
        console.error('Error fetching eligible cases:', err);
        setError(prev => ({ 
          ...prev, 
          cases: 'Failed to load eligible cases. Please try again.' 
        }));
      } finally {
        setLoading(prev => ({ ...prev, cases: false }));
      }
    };
    
    fetchEligibleCases(); // Call the renamed function
  }, []);
  
  // Initialize selected cases from location state
  useEffect(() => {
    if (selectedCaseIds.length > 0 && cases.length > 0) {
      const preSelectedCases = cases.filter(caseItem => 
        selectedCaseIds.includes(caseItem.id)
      );
      setSelectedCases(preSelectedCases);
    }
  }, [selectedCaseIds, cases]);
  
  // Calculate total amount from selected cases
  useEffect(() => {
    const total = selectedCases.reduce((acc, curr) => {
      // Use payout_amount field if it exists
      const amount = curr.payout_amount || 0;
      return acc + amount;
    }, 0);
    setTotalAmount(total);
  }, [selectedCases]);
  
  // Handle case selection
  const handleCaseSelection = (caseItem) => {
    if (selectedCases.some(c => c.id === caseItem.id)) {
      setSelectedCases(selectedCases.filter(c => c.id !== caseItem.id));
    } else {
      setSelectedCases([...selectedCases, caseItem]);
    }
  };
  
  // Handle select all cases
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedCases([]);
    } else {
      setSelectedCases([...cases]);
    }
    setSelectAll(!selectAll);
  };
  
  // Check if create invoice button should be enabled
  const isFormValid = () => {
    return (
      selectedCases.length > 0
    );
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isFormValid()) {
      setError(prev => ({ 
        ...prev, 
        submit: 'Please select at least one case to create an invoice.' 
      }));
      return;
    }
    
    setLoading(prev => ({ ...prev, submit: true }));
    setError(prev => ({ ...prev, submit: '' }));
    setSuccess('');
    
    try {
      // Create invoice with selected case IDs
      const response = await agentApi.createInvoice({
        // payout_id is no longer needed, backend creates it
        case_ids: selectedCases.map(c => c.id)
      });
      
      setSuccess('Invoice created successfully!');
      
      // Navigate to the invoice details page after a brief delay
      setTimeout(() => {
        navigate(`/agent/invoices/${response.data.invoice_id}`);
      }, 2000);
    } catch (err) {
      console.error('Error creating invoice:', err);
      setError(prev => ({ 
        ...prev,
        submit: err.response?.data?.detail || 'Failed to create invoice. Please try again.' 
      }));
    } finally {
      setLoading(prev => ({ ...prev, submit: false }));
    }
  };

  // --- Handlers for Header elements (copied from Products.js) ---
  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const toggleNotifications = () => {
    setShowNotifications(prev => !prev);
  };

  const handleMarkAsRead = async (notificationId) => {
    const notification = notifications.find(n => n.id === notificationId);
    if (!notification || notification.is_read) return;

    try {
      await notificationApi.markAsRead(notificationId);
      setNotifications(prev => prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    if (unreadCount === 0) return;

    try {
      await notificationApi.markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  const formatTimestamp = (timestamp) => {
    try {
      return new Date(timestamp).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
    } catch (e) { return timestamp; }
  };

  // Notifications polling effect (copied from Products.js)
  useEffect(() => {
    let intervalId;
    // --- Start copied content ---
    const fetchNotificationsData = async () => {
      try {
        const countResponse = await notificationApi.getUnreadCount();
        setUnreadCount(countResponse.data.unread_count);
        // Optionally fetch latest few notifications for preview if needed
        // const notificationsResponse = await notificationApi.getNotifications({ limit: 5 });
        // setNotifications(notificationsResponse.data); // Update if you want preview
      } catch (error) {
        console.error('Error fetching notifications count:', error);
      }
     };
    fetchNotificationsData(); // Initial fetch
    intervalId = setInterval(fetchNotificationsData, 60000); // Poll count every minute
    
    // --- End copied content ---
    return () => clearInterval(intervalId); // Cleanup
  }, []);

  // Fetch full notifications when dropdown opens (copied from Products.js)
  useEffect(() => {
    // --- Start copied content ---
    const fetchFullNotifications = async () => {
      if (showNotifications) {
        setLoadingNotifications(true);
        try {
          const notificationsResponse = await notificationApi.getNotifications({ limit: 10 });
          setNotifications(notificationsResponse.data);
        } catch (error) {
          console.error('Error fetching notifications:', error);
          setNotifications([]);
        } finally {
          setLoadingNotifications(false);
        }
      }
     };
    // --- End copied content ---
    fetchFullNotifications();
  }, [showNotifications]);

  // Handle clicks outside notification dropdown (copied from Products.js)
  useEffect(() => {
    // --- Start copied content ---
    const handleClickOutside = (event) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
    };
    // --- End copied content ---
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // Loading state for the whole page
  if (loading.cases) {
    return (
      <div className="agent-dashboard-layout">
        <AgentSidebar />
        <div className="agent-content-wrapper" ref={printableRef}> {/* Add ref here */}
          <Container fluid className="p-4">
            <div className="text-center p-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3">Loading data...</p>
            </div>
          </Container>
        </div>
      </div>
    );
  }

  // Error state
  if (error.cases) {
    return (
      <div className="agent-dashboard-layout">
        <AgentSidebar />
        <div className="agent-content-wrapper">
          <Container fluid className="p-4">
            {/* Removed duplicate Back button */}
            <Alert variant="danger">
              {error.cases}
            </Alert>
          </Container>
        </div>
      </div>
    );
  }

  return (
    <div className="agent-dashboard-layout">
      <AgentSidebar />
      <div className="agent-content-wrapper">
        {/* --- Copied Header Structure from Products.js --- */}
        <header className="agent-header">
          <div className="agent-greeting">
            <span className="agent-greeting-text">Hi, {currentUser?.name || 'Agent'} <span className="agent-greeting-emoji">👋</span></span>
          </div>

          {/* Optional: Add search if needed on this page */}
          {/* <div className="agent-header-search"> ... </div> */}

          <div className="agent-header-actions">
            <div className="agent-header-icon">
              <FaQuestionCircle size={18} />
            </div>

            <div ref={notificationRef} className="position-relative agent-notifications me-3">
              <Button
                variant="link"
                className="text-secondary p-0 position-relative"
                onClick={toggleNotifications}
                id="dropdown-notifications-agent-create"
                aria-expanded={showNotifications}
                title="Notifications"
              >
                <FaBell size={20} />
                {unreadCount > 0 && (
                  <Badge
                    pill
                    bg="danger"
                    className="position-absolute top-0 start-100 translate-middle border border-light"
                    style={{ fontSize: '0.65em', padding: '0.3em 0.5em' }}
                  >
                    {unreadCount > 9 ? '9+' : unreadCount}
                    <span className="visually-hidden">unread notifications</span>
                  </Badge>
                )}
              </Button>

              {/* Notification Dropdown Menu */}
              <Dropdown.Menu
                show={showNotifications}
                className="shadow mt-2 notification-dropdown"
                style={{ minWidth: '350px', maxHeight: '450px', overflowY: 'auto' }}
              >
                <Dropdown.Header className="d-flex justify-content-between align-items-center">
                  Notifications
                  {unreadCount > 0 && (
                    <Button variant="link" size="sm" onClick={handleMarkAllAsRead} className="p-0 text-decoration-none">
                      Mark all read
                    </Button>
                  )}
                </Dropdown.Header>
                <Dropdown.Divider className="my-1" />
                {loadingNotifications ? (
                  <div className="text-center p-3">
                    <Spinner animation="border" size="sm" variant="primary" /> Loading...
                  </div>
                ) : notifications.length > 0 ? (
                  <ListGroup variant="flush">
                    {/* --- Start copied content --- */}
                    {notifications.map(notif => (
                      <ListGroup.Item
                        key={notif.id}
                        action={!notif.is_read} // Make item clickable if unread
                        onClick={() => handleMarkAsRead(notif.id)}
                        style={{
                          cursor: !notif.is_read ? 'pointer' : 'default',
                          backgroundColor: !notif.is_read ? '#f8f9fa' : 'transparent', // Highlight unread
                          borderLeft: !notif.is_read ? '4px solid #0d6efd' : 'none', // Blue left border for unread
                          padding: '0.75rem 1.25rem',
                          borderBottom: '1px solid #eee' // Separator
                        }}
                        className="notification-item" // Add class for potential styling
                      >
                        <div className={`fw-bold ${!notif.is_read ? 'text-primary' : ''}`}>{notif.title || 'Notification'}</div>
                        <div className="text-muted small mb-1">{notif.message || 'No details available.'}</div>
                        <small className="text-muted">{formatTimestamp(notif.created_at)}</small>
                      </ListGroup.Item>
                    ))}
                    {/* --- End copied content --- */}
                  </ListGroup>
                ) : (
                  <Dropdown.ItemText className="text-center text-muted p-3">
                    You're all caught up!
                  </Dropdown.ItemText>
                )}
              </Dropdown.Menu>
            </div>
            <div className="agent-user-profile">
              <div className="agent-avatar">
                <FaUser />
              </div>
              <div className="agent-user-info">
                <span className="agent-user-name">{currentUser?.name || 'Agent User'}</span>
                <button className="agent-logout-btn" onClick={handleLogout}><FaSignOutAlt size={14} /><span>Sign out</span></button>
              </div>
            </div>
          </div>
        </header>
        {/* --- End of Copied Header --- */}
        <Container fluid className="p-4">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <Button 
                variant="outline-primary" 
                as={Link} 
                to={`/agent/invoices/`} 
                className="mb-3"
              >
                <FaArrowLeft className="me-1" /> Back to Invoices
              </Button>
              <h2 className="page-title">Create Invoice</h2>
              <p className="page-subtitle">Select approved cases to generate an invoice</p> {/* Updated subtitle */}
            </div>
          </div>

          {success && (
            <Alert variant="success" className="mb-4">
              <FaCheckCircle className="me-2" /> {success}
            </Alert>
          )}
          
          {error.submit && (
            <Alert variant="danger" className="mb-4">
              {error.submit}
            </Alert>
          )}
          
          <Row>
            

            {/* Cases Table - Takes full width */}
            <Col xs={12}>
              <Card>
                <Card.Header className="bg-transparent d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">Select Eligible Cases</h5> {/* Updated title */}
                  {cases.length > 0 && (
                    <Form.Check
                      type="checkbox"
                      id="select-all-cases"
                      label="Select All"
                      checked={selectAll}
                      onChange={handleSelectAll}
                      className="mb-0"
                    />
                  )}
                </Card.Header>
                <Card.Body>
                  {cases.length > 0 ? (
                    <div className="table-responsive">
                      <Table hover className="custom-table">
                        <thead>
                          <tr>
                            <th width="50">Select</th>
                            <th>Case ID</th>
                            <th>Lead Name</th>
                            <th>Lead Email</th>
                            <th>Lead Phone</th>
                            <th>Application No.</th>
                            <th>Product</th>
                            <th>Status</th>
                            <th>Commission</th>
                            <th>Created At</th>
                          </tr>
                        </thead>
                        <tbody>
                          {cases.map(caseItem => (
                            <tr 
                              key={caseItem.id}
                              className={selectedCases.some(c => c.id === caseItem.id) ? 'selected-row' : ''}
                            >
                              <td>
                                <Form.Check
                                  type="checkbox"
                                  id={`select-case-${caseItem.id}`}
                                  checked={selectedCases.some(c => c.id === caseItem.id)}
                                  onChange={() => handleCaseSelection(caseItem)}
                                />
                              </td>
                              <td>{caseItem.id}</td>
                              <td>{caseItem.lead_name}</td>
                              <td>{caseItem.lead_email}</td>
                              <td>{caseItem.lead_phone}</td>
                              <td>{caseItem.application_no || 'N/A'}</td>
                              <td>{caseItem.product_name || 'N/A'}</td> 
                              <td><Badge>{caseItem.case_status || 'N/A'}</Badge></td> 
                              <td>{formatCurrency(caseItem.payout_amount)}</td>
                              <td>{formatDate(caseItem.created_at)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  ) : (
                    <Alert variant="info">
                      <FaInfoCircle className="me-2" />
                      No eligible cases found for invoice creation. Only cases approved by RM/Backoffice can be included.
                    </Alert>
                  )}
                </Card.Body>
              </Card>
            </Col>
            {/* Invoice Summary Card - Moved to full width above table */}
            <Col xs={12} className="mt-4">
              <Card>
                <Card.Header className="bg-transparent">
                  <h5 className="mb-0">Invoice Summary</h5>
                </Card.Header>
                <Card.Body>
                  <div className="info-item">
                    <span className="info-label">Cases Selected</span>
                    <span className="info-value">{selectedCases.length} of {cases.length}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Total Amount</span>
                    <span className="info-value fw-bold">{formatCurrency(totalAmount)}</span>
                  </div>
                  
                  <Button
                    variant="success"
                    className="w-100 mt-3"
                    onClick={handleSubmit}
                    disabled={!isFormValid() || loading.submit}
                  >
                    {loading.submit ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Creating Invoice...
                      </>
                    ) : (
                      <>
                        <FaFileInvoice className="me-2" /> Create Invoice
                      </>
                    )}
                  </Button>
                </Card.Body>
              </Card> {/* Add missing closing tag */}
            </Col>
          </Row>
        </Container>
      </div>
      
      <style jsx>{`
        .page-title {
          font-size: 1.5rem;
          font-weight: 600;
          margin-bottom: 0.5rem;
          color: #1e293b;
        }
        
        .page-subtitle {
          color: #64748b;
          margin-bottom: 0;
        }
        
        .info-item {
          display: flex;
          justify-content: space-between;
          margin-bottom: 1rem;
          padding-bottom: 1rem;
          border-bottom: 1px solid #e2e8f0;
        }
        
        .info-item:last-child {
          margin-bottom: 0;
          padding-bottom: 0;
          border-bottom: none;
        }
        
        .info-label {
          color: #64748b;
          font-weight: 500;
        }
        
        .info-value {
          color: #1e293b;
          font-weight: 600;
        }
        
        .custom-table thead th {
          background-color: #f8fafc;
          color: #64748b;
          font-weight: 600;
          font-size: 0.875rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          border-bottom: 1px solid #e2e8f0;
          padding: 0.75rem 1rem;
        }
        
        .custom-table tbody td {
          padding: 1rem;
          vertical-align: middle;
          border-bottom: 1px solid #e2e8f0;
          color: #334155;
          font-size: 0.9375rem;
        }
        
        .custom-table tbody tr:hover {
          background-color: #f1f5f9;
        }
        
        .selected-row {
          background-color: #ebf5ff !important;
        }
        
        .selected-row:hover {
          background-color: #dbeafe !important;
        }
      `}</style>
    </div>
  );
};

export default CreateInvoice;
